import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/ui/ui-button';
import { SERVICE_OFFERS } from '../content/services';
import { navigate } from '../router';
import { boxSizingStyles, fullBleedStyles } from '../styles/lit-shared';

const CALENDAR_URL = 'https://calendar.app.google/3TMX3h7SGT1wkdp87';
const SUBSTACK_URL = 'https://alejandrosn.substack.com';
const BLUNTICLE_URL = 'https://blunticle.alejandrosn.com';

const PROJECTS = [
  {
    name: 'SubbySwap',
    label: 'Producto propio',
    description:
      'Producto propio para encontrar y verificar colaboraciones entre newsletters de Substack.',
    features: [
      'Matching por temática y tamaño.',
      'Verificación automática de recomendaciones.',
      'Estados, contratos y avisos.',
      'Login mediante magic link.',
      'Datos públicos, sin almacenar credenciales.',
    ],
    cta: 'Ver proyecto',
    path: '/subbyswap',
  },
  {
    name: 'Blunticle',
    label: 'Herramienta propia',
    description:
      'Herramienta propia para generar contenido estructurado a partir de una idea, una URL o un producto.',
    features: [
      'Flujo multiagente.',
      'Investigación y extracción de contexto.',
      'Redacción y revisión.',
      'Generación de piezas para publicación.',
      'Aplicación funcional en producción.',
    ],
    cta: 'Probar Blunticle',
    href: BLUNTICLE_URL,
  },
  {
    name: 'Traviapp',
    label: 'Producto propio',
    description:
      'El link-in-bio que crece contigo: perfil, enlaces, secciones, suscriptores y analítica — todo en una página que se siente como tuya.',
    features: [
      'Perfil único, con tu marca y adaptable a cualquier dispositivo.',
      'Enlaces ilimitados, reordenables al instante.',
      'Secciones con texto, imagen, vídeo embed y CTA.',
      'Iconos sociales nativos y captura de suscriptores.',
      'Temas, SEO, Open Graph y assets en red global.',
    ],
    cta: 'Ver proyecto',
    path: '/traviapp',
  },
  {
    name: 'NFC Pop',
    label: 'En desarrollo',
    description:
      'Soporte físico para comercios: el cliente toca el NFC o escanea el QR y llega a una URL que el establecimiento puede cambiar cuando quiera.',
    features: [
      'Acceso por NFC o QR desde un soporte físico.',
      'URL dinámica por temporada, oferta o campaña.',
      'Destinos a reseñas, redes o lo que el negocio necesite.',
      'Panel para cambiar el enlace sin reimprimir nada.',
      'Altas y ventas con Stripe, operadas desde un bot de Telegram.',
    ],
    cta: 'Ver proyecto',
    path: '/tag-nfc',
  },
] as const;

@customElement('landing-page')
export class LandingPage extends LitElement {
  private _revealObserver: IntersectionObserver | null = null;

  private goTo(path: string) {
    void navigate(path);
  }

  firstUpdated() {
    this._setupReveal();
  }

  disconnectedCallback() {
    this._revealObserver?.disconnect();
    this._revealObserver = null;
    super.disconnectedCallback();
  }

  private _setupReveal() {
    const root = this.shadowRoot;
    if (!root) return;

    const els = root.querySelectorAll('.reveal');
    if (!els.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    this._revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this._revealObserver?.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    els.forEach((el) => this._revealObserver!.observe(el));
  }

  static styles = [
    boxSizingStyles,
    fullBleedStyles,
    css`
      :host {
        display: block;
        width: 100%;
      }

      /* —— Full-bleed cinematic hero —— */
      .hero {
        margin-top: calc(-1 * var(--space-xl));
        margin-bottom: var(--space-2xl);
        min-height: calc(100dvh - var(--header-height));
        display: flex;
        align-items: center;
        padding: var(--space-2xl) var(--space-lg);
        overflow: hidden;
        background:
          radial-gradient(ellipse 80% 60% at 20% 40%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 60% 50% at 80% 20%, var(--glow-cyan), transparent 50%),
          radial-gradient(ellipse 50% 40% at 70% 80%, var(--glow-fuchsia), transparent 45%),
          radial-gradient(ellipse 40% 100% at 0% 50%, var(--glow-violet), transparent 60%),
          radial-gradient(ellipse 40% 100% at 100% 50%, var(--glow-cyan), transparent 60%),
          var(--color-bg);
      }

      .hero-orb {
        position: absolute;
        border-radius: 50%;
        filter: blur(60px);
        pointer-events: none;
        will-change: transform;
        animation: orb-drift 14s var(--ease-out) infinite alternate;
      }

      .hero-orb--1 {
        top: 8%;
        left: 5%;
        width: min(420px, 55vw);
        height: min(420px, 55vw);
        background: var(--glow-violet);
        animation-duration: 16s;
      }

      .hero-orb--2 {
        top: 20%;
        right: 0;
        width: min(360px, 45vw);
        height: min(360px, 45vw);
        background: var(--glow-cyan);
        animation-duration: 12s;
        animation-delay: -4s;
      }

      .hero-orb--3 {
        bottom: 5%;
        left: 40%;
        width: min(280px, 40vw);
        height: min(280px, 40vw);
        background: var(--glow-fuchsia);
        animation-duration: 18s;
        animation-delay: -8s;
      }

      @keyframes orb-drift {
        from {
          transform: translate(0, 0) scale(1);
        }
        to {
          transform: translate(24px, -18px) scale(1.08);
        }
      }

      .hero-content {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: var(--max-width);
        margin: 0 auto;
        display: grid;
        gap: var(--space-2xl);
        align-items: center;
      }

      @media (min-width: 860px) {
        .hero-content {
          grid-template-columns: 1.15fr 0.85fr;
          gap: var(--space-2xl) var(--space-xl);
        }
      }

      .hero-copy {
        text-align: left;
      }

      .brand-mark {
        display: block;
        margin-bottom: var(--space-md);
        font-family: var(--font-sans);
        font-size: clamp(1.125rem, 2.5vw, 1.375rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--color-text);
        opacity: 0;
        animation: hero-enter 700ms var(--ease-out) 80ms forwards;
      }

      .brand-mark span {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .hero h1 {
        font-size: clamp(2.5rem, 7vw, 4.25rem);
        font-weight: 700;
        letter-spacing: -0.04em;
        line-height: 1.08;
        margin: 0 0 var(--space-md);
        color: var(--color-text);
        opacity: 0;
        animation: hero-enter 700ms var(--ease-out) 180ms forwards;
      }

      .hero h1 .title-grad {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .hero h1 .accent {
        color: var(--color-accent);
        white-space: nowrap;
      }

      .hero-lead {
        font-size: 1.125rem;
        color: var(--color-text-muted);
        max-width: 52ch;
        margin: 0 0 var(--space-md);
        line-height: 1.6;
        opacity: 0;
        animation: hero-enter 700ms var(--ease-out) 280ms forwards;
      }

      .hero-scope {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
        margin: 0 0 var(--space-xl);
        padding: 0;
        list-style: none;
        max-width: 44ch;
        opacity: 0;
        animation: hero-enter 700ms var(--ease-out) 330ms forwards;
      }

      .hero-scope li {
        display: grid;
        grid-template-columns: 8.75rem minmax(0, 1fr);
        gap: var(--space-sm);
        align-items: baseline;
        line-height: 1.4;
      }

      .scope-name {
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-accent);
      }

      .scope-ex {
        font-size: 0.875rem;
        color: var(--color-text-muted);
      }

      @media (max-width: 420px) {
        .hero-scope li {
          grid-template-columns: 1fr;
          gap: 0.1rem;
        }
      }

      .hero-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-md);
        opacity: 0;
        animation: hero-enter 700ms var(--ease-out) 380ms forwards;
      }

      @keyframes hero-enter {
        from {
          opacity: 0;
          transform: translateY(18px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Timeline anchor */
      .hero-timeline {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 0;
        margin: 0;
        padding: 0 0 0 var(--space-lg);
        list-style: none;
        opacity: 0;
        animation: hero-enter 800ms var(--ease-out) 480ms forwards;
      }

      .hero-timeline::before {
        content: '';
        position: absolute;
        left: 0.35rem;
        top: 0.5rem;
        bottom: 0.5rem;
        width: 2px;
        background: linear-gradient(
          to bottom,
          var(--color-accent),
          var(--color-primary-light),
          rgb(34 211 238 / 20%)
        );
        transform-origin: top;
        animation: timeline-grow 1.1s var(--ease-out) 560ms both;
      }

      @keyframes timeline-grow {
        from {
          transform: scaleY(0);
        }
        to {
          transform: scaleY(1);
        }
      }

      .timeline-step {
        position: relative;
        padding: var(--space-sm) 0 var(--space-md);
        opacity: 0;
        animation: hero-enter 600ms var(--ease-out) both;
      }

      .timeline-step:nth-child(1) {
        animation-delay: 620ms;
      }
      .timeline-step:nth-child(2) {
        animation-delay: 740ms;
      }
      .timeline-step:nth-child(3) {
        animation-delay: 860ms;
      }
      .timeline-step:nth-child(4) {
        animation-delay: 980ms;
      }

      .timeline-step::before {
        content: '';
        position: absolute;
        left: calc(-1 * var(--space-lg) + 0.1rem);
        top: calc(var(--space-sm) + 0.35rem);
        width: 0.7rem;
        height: 0.7rem;
        border-radius: 50%;
        background: var(--color-bg);
        border: 2px solid var(--color-accent);
        box-shadow: 0 0 12px rgb(34 211 238 / 45%);
      }

      .timeline-label {
        display: block;
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--color-accent);
        margin-bottom: 0.2rem;
      }

      .timeline-title {
        display: block;
        font-size: 1.0625rem;
        font-weight: 600;
        letter-spacing: -0.02em;
        color: var(--color-text);
      }

      .timeline-desc {
        display: block;
        margin-top: 0.15rem;
        font-size: 0.875rem;
        color: var(--color-text-muted);
        line-height: 1.45;
        max-width: 28ch;
      }

      @media (max-width: 859px) {
        .hero {
          margin-top: calc(-1 * var(--space-lg));
          padding: var(--space-xl) var(--space-md);
          min-height: auto;
          padding-block: var(--space-2xl);
        }

        .hero-timeline {
          padding-top: var(--space-sm);
          border-top: 1px solid var(--color-border);
        }
      }

      /* —— Shared section language —— */
      .section {
        padding: var(--space-2xl) var(--space-lg);
        max-width: var(--max-width);
        margin-inline: auto;
      }

      .section-label {
        display: block;
        margin-bottom: var(--space-md);
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: var(--color-accent);
      }

      .section-title {
        font-size: clamp(1.75rem, 4vw, 2.5rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        line-height: 1.15;
        margin: 0 0 var(--space-sm);
        color: var(--color-text);
      }

      .section-title .title-grad {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .section-title .accent {
        color: var(--color-accent);
      }

      .section-lead {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.65;
        margin: 0 0 var(--space-xl);
        max-width: 52ch;
      }

      .reveal {
        opacity: 0;
        transform: translateY(18px);
        transition:
          opacity 700ms var(--ease-out),
          transform 700ms var(--ease-out);
      }

      .reveal.is-visible {
        opacity: 1;
        transform: translateY(0);
      }

      .reveal-delay-1 {
        transition-delay: 80ms;
      }
      .reveal-delay-2 {
        transition-delay: 160ms;
      }
      .reveal-delay-3 {
        transition-delay: 240ms;
      }
      .reveal-delay-4 {
        transition-delay: 320ms;
      }

      /* —— Full-bleed bands —— */
      .band {
        position: relative;
        padding: var(--space-2xl) var(--space-lg);
        /* visible so edge blur feathers can spill into the page */
        overflow: visible;
      }

      /* Soft blur feathers at top/bottom — blend band ink into page bg */
      .band::before,
      .band::after {
        content: '';
        position: absolute;
        left: -1.5rem;
        right: -1.5rem;
        height: 5rem;
        z-index: 0;
        pointer-events: none;
        background: var(--color-bg);
        filter: blur(32px);
        opacity: 0.9;
      }

      .band::before {
        top: -2.5rem;
      }

      .band::after {
        bottom: -2.5rem;
      }

      .band-inner {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: var(--max-width);
        margin: 0 auto;
      }

      .band--roadmap {
        margin-bottom: var(--space-md);
        background:
          radial-gradient(ellipse 70% 80% at 0% 50%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 50% 60% at 100% 80%, var(--glow-cyan), transparent 50%),
          radial-gradient(ellipse 40% 100% at 0% 50%, var(--glow-violet), transparent 60%),
          radial-gradient(ellipse 40% 100% at 100% 50%, var(--glow-cyan), transparent 60%),
          var(--color-bg);
      }

      .band--roadmap .band-inner {
        padding-left: var(--space-xl);
        border-left: 2px solid transparent;
        border-image: linear-gradient(
            to bottom,
            var(--color-accent),
            var(--color-primary-light),
            transparent
          )
          1;
      }

      .band--roadmap .band-copy {
        max-width: 60ch;
      }

      .band--roadmap p {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.65;
        margin: 0 0 var(--space-md);
      }

      .band--roadmap p:last-of-type {
        margin-bottom: var(--space-lg);
      }

      .band--cta {
        margin-top: var(--space-xl);
        margin-bottom: var(--space-xl);
        text-align: center;
        background:
          radial-gradient(ellipse 60% 70% at 30% 40%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 50% 60% at 75% 60%, var(--glow-cyan), transparent 50%),
          radial-gradient(ellipse 40% 100% at 0% 50%, var(--glow-violet), transparent 60%),
          radial-gradient(ellipse 40% 100% at 100% 50%, var(--glow-cyan), transparent 60%),
          var(--color-bg);
      }

      .band--cta .section-title {
        margin-bottom: var(--space-md);
      }

      .band--cta .band-lead {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.65;
        margin: 0 auto var(--space-xl);
        max-width: 48ch;
      }

      .band-orb {
        position: absolute;
        border-radius: 50%;
        filter: blur(70px);
        pointer-events: none;
        animation: orb-drift 18s var(--ease-out) infinite alternate;
      }

      .band-orb--1 {
        top: -20%;
        right: 10%;
        width: min(320px, 40vw);
        height: min(320px, 40vw);
        background: var(--glow-fuchsia);
        opacity: 0.85;
      }

      .band-orb--2 {
        bottom: -30%;
        left: 5%;
        width: min(280px, 35vw);
        height: min(280px, 35vw);
        background: var(--glow-cyan);
        animation-delay: -6s;
      }

      .links-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-md);
        justify-content: center;
      }

      /* —— Process rail —— */
      .process-rail {
        position: relative;
        margin: 0;
        padding: 0 0 0 var(--space-lg);
        list-style: none;
      }

      .process-rail::before {
        content: '';
        position: absolute;
        left: 0.35rem;
        top: 0.6rem;
        bottom: 0.6rem;
        width: 2px;
        background: linear-gradient(
          to bottom,
          var(--color-accent),
          var(--color-primary-light),
          rgb(34 211 238 / 15%)
        );
      }

      .process-step {
        position: relative;
        padding: var(--space-sm) 0 var(--space-lg);
      }

      .process-step::before {
        content: '';
        position: absolute;
        left: calc(-1 * var(--space-lg) + 0.1rem);
        top: calc(var(--space-sm) + 0.4rem);
        width: 0.7rem;
        height: 0.7rem;
        border-radius: 50%;
        background: var(--color-bg);
        border: 2px solid var(--color-accent);
        box-shadow: 0 0 12px rgb(34 211 238 / 40%);
      }

      .process-step .step-label {
        display: block;
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--color-accent);
        margin-bottom: 0.25rem;
      }

      .process-step h3 {
        margin: 0 0 0.35rem;
        font-size: 1.125rem;
        font-weight: 600;
        letter-spacing: -0.02em;
        color: var(--color-text);
      }

      .process-step p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.975rem;
        line-height: 1.6;
        max-width: 58ch;
      }

      /* —— Open feature grids —— */
      .open-grid {
        display: grid;
        gap: 0;
        grid-template-columns: 1fr;
        border-top: 1px solid var(--color-border);
      }

      @media (min-width: 700px) {
        .open-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      .open-item {
        padding: var(--space-lg) var(--space-md) var(--space-lg) 0;
        border-bottom: 1px solid var(--color-border);
      }

      @media (min-width: 700px) {
        .open-item:nth-child(odd) {
          padding-right: var(--space-xl);
          border-right: 1px solid var(--color-border);
        }

        .open-item:nth-child(even) {
          padding-left: var(--space-xl);
        }
      }

      .open-item .item-label {
        display: block;
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--color-accent);
        margin-bottom: var(--space-xs);
      }

      .open-item h3 {
        margin: 0 0 var(--space-xs);
        font-size: 1.125rem;
        font-weight: 600;
        letter-spacing: -0.02em;
        color: var(--color-text);
      }

      .open-item p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.6;
      }

      .project-card {
        display: flex;
        flex-direction: column;
        min-height: 100%;
      }

      .project-card h3 {
        font-size: 1.375rem;
      }

      .project-features {
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
        margin: var(--space-sm) 0 var(--space-md);
        padding: 0;
        list-style: none;
      }

      .project-features li {
        position: relative;
        padding-left: 1rem;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .project-features li::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0.55em;
        width: 0.35rem;
        height: 0.35rem;
        border-radius: 50%;
        background: var(--color-accent);
        box-shadow: 0 0 8px rgb(34 211 238 / 40%);
      }

      .project-card ui-button {
        margin-top: auto;
        align-self: flex-start;
      }

      .agency-note {
        max-width: 58ch;
        margin: 0 0 var(--space-lg);
        padding-left: var(--space-lg);
        border-left: 2px solid var(--color-accent);
        color: var(--color-text);
        font-size: 1.0625rem;
        line-height: 1.65;
      }

      @media (max-width: 859px) {
        .band,
        .section {
          padding: var(--space-2xl) var(--space-md);
        }

        .band--roadmap .band-inner {
          padding-left: var(--space-lg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .hero-orb,
        .band-orb {
          animation: none;
        }

        .brand-mark,
        .hero h1,
        .hero-lead,
        .hero-scope,
        .hero-actions,
        .hero-timeline,
        .timeline-step,
        .hero-timeline::before {
          animation: none;
          opacity: 1;
          transform: none;
        }

        .reveal {
          opacity: 1;
          transform: none;
          transition: none;
        }
      }
    `,
  ];

  render() {
    return html`
      <section class="hero full-bleed" aria-label="Presentación">
        <span class="hero-orb hero-orb--1" aria-hidden="true"></span>
        <span class="hero-orb hero-orb--2" aria-hidden="true"></span>
        <span class="hero-orb hero-orb--3" aria-hidden="true"></span>

        <div class="hero-content">
          <div class="hero-copy">
            <p class="brand-mark"><span>AlejandroSN</span></p>
            <h1><span class="title-grad">Construyo herramientas digitales y automatizaciones para</span> <span class="accent">problemas reales</span></h1>
            <p class="hero-lead">
              Soy Alejandro Serrano, desarrollador especializado en JavaScript, Cloudflare y
              automatización. Construyo productos completos y soluciones concretas, desde la
              idea hasta su puesta en producción.
            </p>
            <ul class="hero-scope" aria-label="Qué podemos construir">
              ${SERVICE_OFFERS.map(
                (item) => html`
                  <li>
                    <span class="scope-name">${item.name}</span>
                    <span class="scope-ex">${item.example}</span>
                  </li>
                `,
              )}
            </ul>
            <div class="hero-actions">
              <ui-button href=${CALENDAR_URL} variant="primary">Reservar una llamada</ui-button>
              <ui-button variant="secondary" @ui-click=${() => this.goTo('/servicios')}
                >Ver qué puedo construir</ui-button
              >
              <ui-button href=${SUBSTACK_URL} variant="secondary" target="_blank">Leer mi Substack</ui-button>
            </div>
          </div>

          <ol class="hero-timeline" aria-label="Proceso">
            <li class="timeline-step">
              <span class="timeline-label">01</span>
              <span class="timeline-title">Idea</span>
              <span class="timeline-desc">Traes el problema y el contexto.</span>
            </li>
            <li class="timeline-step">
              <span class="timeline-label">02</span>
              <span class="timeline-title">Roadmap</span>
              <span class="timeline-desc">Alineamos alcance antes de programar.</span>
            </li>
            <li class="timeline-step">
              <span class="timeline-label">03</span>
              <span class="timeline-title">Build</span>
              <span class="timeline-desc">Construyo con checkpoints. Ves el progreso, no promesas.</span>
            </li>
            <li class="timeline-step">
              <span class="timeline-label">04</span>
              <span class="timeline-title">Ship</span>
              <span class="timeline-desc">Lanzas, mides y evolucionamos juntos.</span>
            </li>
          </ol>
        </div>
      </section>

      <section class="section" aria-label="Proyectos reales">
        <span class="section-label reveal">Proyectos</span>
        <h2 class="section-title reveal reveal-delay-1">
          Proyectos <span class="accent">reales</span>
        </h2>
        <p class="section-lead reveal reveal-delay-2">
          Productos propios. Problemas concretos, no demos.
        </p>
        <div class="open-grid">
          ${PROJECTS.map(
            (project, i) => html`
              <article class="open-item project-card reveal ${i > 0 ? `reveal-delay-${i}` : ''}">
                <span class="item-label">${project.label}</span>
                <h3>${project.name}</h3>
                <p>${project.description}</p>
                <ul class="project-features">
                  ${project.features.map((feature) => html`<li>${feature}</li>`)}
                </ul>
                ${'href' in project
                  ? html`<ui-button href=${project.href} variant="primary">${project.cta}</ui-button>`
                  : html`<ui-button variant="secondary" @ui-click=${() => this.goTo(project.path)}
                      >${project.cta}</ui-button
                    >`}
              </article>
            `,
          )}
        </div>
      </section>

      <section class="section" aria-label="Colaboración técnica para agencias">
        <span class="section-label reveal">Agencias</span>
        <h2 class="section-title reveal reveal-delay-1">
          Colaboración técnica para <span class="accent">agencias</span>
        </h2>
        <p class="section-lead reveal reveal-delay-2">
          Si una agencia tiene un proyecto que requiere desarrollo, integraciones o
          automatización y no quiere asumirlo internamente, puedo encargarme de la
          ejecución técnica con un alcance cerrado.
        </p>
        <p class="agency-note reveal reveal-delay-3">
          No sustituyo la relación de la agencia con su cliente ni contacto
          directamente con él sin autorización.
        </p>
        <div class="reveal reveal-delay-4">
          <ui-button variant="secondary" @ui-click=${() => this.goTo('/agencias')}
            >Ver colaboración para agencias</ui-button
          >
        </div>
      </section>

      <section class="band band--roadmap full-bleed" aria-label="Hoja de ruta">
        <div class="band-inner reveal">
          <span class="section-label">01 · Idea</span>
          <h2 class="section-title">
            Antes de escribir una sola <span class="title-grad">línea de código</span>
          </h2>
          <div class="band-copy">
            <p>
              Lo primero que hacemos es una reunión de hoja de ruta. Sin código. Sin prisas.
              Nos sentamos a entender tu idea al detalle: qué problema resuelve, para quién,
              qué entra ahora y qué puede esperar.
            </p>
            <p>
              Solo cuando los dos tenemos clarísimo qué vamos a construir y por qué, empezamos
              a trabajar. Esa alineación es lo que evita el trabajo a medias.
            </p>
          </div>
          <ui-button href=${CALENDAR_URL} variant="primary">Hablar sobre tu idea</ui-button>
        </div>
      </section>

      <section class="section" aria-label="Cómo funciona">
        <span class="section-label reveal">02 · Roadmap</span>
        <h2 class="section-title reveal reveal-delay-1">Cómo funciona</h2>
        <p class="section-lead reveal reveal-delay-2">
          Un proceso directo, sin reuniones infinitas ni entregas misteriosas a mitad de camino.
        </p>
        <ol class="process-rail">
          <li class="process-step reveal">
            <span class="step-label">01</span>
            <h3>La llamada de hoja de ruta</h3>
            <p>
              Entendemos juntos tu idea, definimos el alcance de la primera versión
              lanzable y acordamos qué se construye. Nada de suposiciones: todo queda
              claro antes de empezar.
            </p>
          </li>
          <li class="process-step reveal reveal-delay-1">
            <span class="step-label">02</span>
            <h3>El brief acordado</h3>
            <p>
              Redacto un documento que recoge lo hablado: funcionalidades, tecnología y
              criterios de éxito. Lo firmas tú. Si no lo firmas, no empiezo.
            </p>
          </li>
          <li class="process-step reveal reveal-delay-2">
            <span class="step-label">03</span>
            <h3>Construcción con checkpoints</h3>
            <p>
              Trabajo en sprints cortos con checkpoints periódicos. Ves el progreso
              real, no promesas. Al entregar, tienes algo que puedes lanzar.
            </p>
          </li>
          <li class="process-step reveal reveal-delay-3">
            <span class="step-label">04</span>
            <h3>Entrega y transferencia</h3>
            <p>
              Código, documentación y contexto completo. No dependes de mí para
              seguir creciendo — aunque suelo estar cerca si me necesitas.
            </p>
          </li>
          <li class="process-step reveal reveal-delay-4">
            <span class="step-label">05</span>
            <h3>Seguimiento y evolutivos</h3>
            <p>
              Lanzar es el principio, no el final. Vemos qué impacto ha tenido lo
              lanzado, qué funciona, qué no, y decidimos juntos hacia dónde pivotar,
              qué ajustar o qué construir a continuación.
            </p>
          </li>
        </ol>
      </section>

      <section class="section" aria-label="Por qué así">
        <span class="section-label reveal">03 · Build</span>
        <h2 class="section-title reveal reveal-delay-1">
          Por qué <span class="accent">así</span>
        </h2>
        <p class="section-lead reveal reveal-delay-2">
          Foco y alcance cerrado. Para tener algo real sin morir en el intento, y sin
          un número mágico de por medio.
        </p>
        <div class="open-grid">
          <article class="open-item reveal">
            <span class="item-label">Foco</span>
            <h3>Foco brutal desde el día uno</h3>
            <p>
              La primera versión es la más pequeña que sirve para aprender. Esa
              restricción es una ventaja, no un límite.
            </p>
          </article>
          <article class="open-item reveal reveal-delay-1">
            <span class="item-label">Stack</span>
            <h3>Tecnología que escala</h3>
            <p>
              Uso el stack adecuado para cada proyecto, siempre orientado al edge y a costes
              mínimos en arranque. Lo que construimos aguanta el crecimiento si llega.
            </p>
          </article>
          <article class="open-item reveal reveal-delay-2">
            <span class="item-label">Scope</span>
            <h3>Sin sorpresas a mitad</h3>
            <p>
              El scope está acordado desde el principio. Si surge algo nuevo, lo hablamos
              y decidimos juntos. Nada de cambios silenciosos que se acumulan y explotan.
            </p>
          </article>
          <article class="open-item reveal reveal-delay-3">
            <span class="item-label">Criterio</span>
            <h3>Velocidad con criterio</h3>
            <p>
              Rápido no significa chapucero. Cada decisión técnica tiene su porqué. Y si
              hay deuda técnica, sabes exactamente dónde está y por qué existe.
            </p>
          </article>
        </div>
      </section>

      <section class="section" aria-label="Después del lanzamiento">
        <span class="section-label reveal">04 · Ship</span>
        <h2 class="section-title reveal reveal-delay-1">Después del lanzamiento</h2>
        <p class="section-lead reveal reveal-delay-2">
          Lanzar es el punto de partida. Lo que pasa después es donde empieza la parte interesante.
        </p>
        <div class="open-grid">
          <article class="open-item reveal">
            <span class="item-label">Impacto</span>
            <h3>Seguimiento del impacto</h3>
            <p>
              Revisamos juntos cómo responden los usuarios, qué métricas importan y
              qué nos dice el comportamiento real sobre lo que funciona y lo que no.
            </p>
          </article>
          <article class="open-item reveal reveal-delay-1">
            <span class="item-label">Evolutivos</span>
            <h3>Evolutivos sin drama</h3>
            <p>
              Las funcionalidades que se quedaron fuera del alcance acordado tienen su momento.
              Priorizamos en función del aprendizaje real, no de corazonadas.
            </p>
          </article>
          <article class="open-item reveal reveal-delay-2">
            <span class="item-label">Ajustes</span>
            <h3>Ajustes y correcciones</h3>
            <p>
              Si algo no encaja como esperábamos, lo cambiamos. Sin ego, sin
              burocracia. El código es una herramienta, no un monumento.
            </p>
          </article>
          <article class="open-item reveal reveal-delay-3">
            <span class="item-label">Pivote</span>
            <h3>Cambios de dirección</h3>
            <p>
              A veces el mercado te dice que pivotes. Cuando eso pasa, tenemos el
              contexto técnico para mover el timón rápido sin tirar lo construido.
            </p>
          </article>
        </div>
      </section>

      <section class="band band--cta full-bleed" aria-label="Saber más">
        <span class="band-orb band-orb--1" aria-hidden="true"></span>
        <span class="band-orb band-orb--2" aria-hidden="true"></span>
        <div class="band-inner reveal">
          <span class="section-label">Siguiente paso</span>
          <h2 class="section-title">
            ¿Quieres saber <span class="title-grad">más</span>?
          </h2>
          <p class="band-lead">
            En mi Substack escribo sobre producto y lo que aprendo construyendo.
            Sin filtros, sin postureo. O si prefieres, reserva una llamada y lo hablamos.
          </p>
          <div class="links-actions">
            <ui-button href=${CALENDAR_URL} variant="primary">Reservar una llamada</ui-button>
            <ui-button href=${SUBSTACK_URL} variant="secondary" target="_blank">Mi Substack</ui-button>
            <ui-button variant="ghost" @ui-click=${() => this.goTo('/blunticle')}>Conoce Blunticle</ui-button>
          </div>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landing-page': LandingPage;
  }
}
